import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useApp } from '../AppContext';
import { PageHeader } from '../components/Layout';
import { Card, SectionTitle, Field, Spinner, ErrorNote, RemoveButton } from '../components/ui';
import { normMeasurements } from '../lib/format';

export default function SiteVisit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast, run } = useApp();

  const { data, error, loading, reload } = useAsync(() => Promise.all([
    api.get('/projects/' + id),
    api.get(`/projects/${id}/site-visit`),
    api.get('/settings'),
  ]), [id]);

  const [scheduledAt, setScheduledAt] = useState('');
  const [measurements, setMeasurements] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [scopeNotes, setScopeNotes] = useState('');
  const [busy, setBusy] = useState(false);

  // Seed the editors once the server data lands.
  useEffect(() => {
    if (!data) return;
    const [, existing, settings] = data;
    const sv = existing || { measurements: [{ label: 'Roof area', value: '2500', unit: 'sft' }], materials: [] };
    setScheduledAt(sv.scheduledAt ? String(sv.scheduledAt).slice(0, 10) : '');
    setMeasurements(normMeasurements(sv.measurements));
    setMaterials([...(sv.materials || [])]);
    setScopeNotes(sv.scopeNotes || (existing ? '' : settings?.defaultScopeNotes || ''));
  }, [data]);

  if (loading) return <Spinner label="Loading site visit" />;
  if (error) return <div className="view"><ErrorNote onRetry={reload}>{error}</ErrorNote></div>;
  const [project, existing] = data;

  const patchMeas = (i, k, v) => setMeasurements(m => m.map((x, j) => j === i ? { ...x, [k]: v } : x));

  async function save(complete) {
    setBusy(true);
    await run(async () => {
      await api.post(`/projects/${id}/site-visit`, {
        scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        completedAt: complete ? new Date().toISOString() : (existing?.completedAt || null),
        measurements: measurements.filter(m => m.label.trim()).map(m => ({
          label: m.label.trim(), value: m.value, unit: m.unit,
        })),
        materials: materials.filter(Boolean),
        scopeNotes,
      });
      toast(complete ? 'Visit completed' : 'Saved');
      navigate(`/project/${id}`);
    });
    setBusy(false);
  }

  return (
    <>
      <PageHeader title="Site visit" sub={project.customer?.name || ''} back={`/project/${id}`} />
      <div className="view">
        <div className="split">
          <Card>
            <SectionTitle>Schedule</SectionTitle>
            <div style={{ marginTop: 'var(--s-3)' }}>
              <Field label="Scheduled date" id="sch">
                <input id="sch" className="fi" type="date" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} />
              </Field>
            </div>
          </Card>

          <Card>
            <SectionTitle sub="Free-form — record what matters for this job">Measurements</SectionTitle>
            <div className="col" style={{ gap: 'var(--s-2)', marginTop: 'var(--s-3)' }}>
              {measurements.map((m, i) => (
                <div className="row" key={i} style={{ gap: 'var(--s-2)' }}>
                  <input className="fi" style={{ flex: 2 }} placeholder="Field (e.g. Roof area)"
                    aria-label={`Measurement ${i + 1} name`}
                    value={m.label} onChange={e => patchMeas(i, 'label', e.target.value)} />
                  <input className="fi" style={{ flex: 1 }} placeholder="Value"
                    aria-label={`Measurement ${i + 1} value`}
                    value={m.value} onChange={e => patchMeas(i, 'value', e.target.value)} />
                  <input className="fi" style={{ flex: 1 }} placeholder="Unit"
                    aria-label={`Measurement ${i + 1} unit`}
                    value={m.unit} onChange={e => patchMeas(i, 'unit', e.target.value)} />
                  <RemoveButton label={`Remove measurement ${m.label || i + 1}`}
                    onClick={() => setMeasurements(x => x.filter((_, j) => j !== i))} />
                </div>
              ))}
            </div>
            <button type="button" className="btn sm" style={{ marginTop: 'var(--s-3)' }}
              onClick={() => setMeasurements(m => [...m, { label: '', value: '', unit: '' }])}>
              ＋ Add measurement
            </button>
          </Card>

          <Card>
            <SectionTitle>Materials needed</SectionTitle>
            <div className="col" style={{ gap: 'var(--s-2)', marginTop: 'var(--s-3)' }}>
              {materials.map((m, i) => (
                <div className="row" key={i} style={{ gap: 'var(--s-2)' }}>
                  <input className="fi" placeholder="Material" aria-label={`Material ${i + 1}`}
                    value={m} onChange={e => setMaterials(x => x.map((y, j) => j === i ? e.target.value : y))} />
                  <RemoveButton label={`Remove material ${m || i + 1}`}
                    onClick={() => setMaterials(x => x.filter((_, j) => j !== i))} />
                </div>
              ))}
            </div>
            <button type="button" className="btn sm" style={{ marginTop: 'var(--s-3)' }}
              onClick={() => setMaterials(m => [...m, ''])}>
              ＋ Add material
            </button>
          </Card>

          <Card className="span-all">
            <SectionTitle>Scope notes</SectionTitle>
            <textarea className="fi" rows={6} style={{ marginTop: 'var(--s-3)' }}
              aria-label="Scope notes"
              placeholder="Describe what work needs to be done…"
              value={scopeNotes} onChange={e => setScopeNotes(e.target.value)} />
          </Card>
        </div>

        <div className="btn-row">
          <button className="btn" disabled={busy} onClick={() => save(false)}>Save (mark scheduled)</button>
          <button className="btn pri" disabled={busy} onClick={() => save(true)}>Save &amp; mark completed</button>
        </div>
      </div>
    </>
  );
}
